import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ParentAccount() {
  const { user, apiFetch, logout } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Kids state
  const [kids, setKids] = useState([]);
  const [kidsLoading, setKidsLoading] = useState(true);
  const [kidEdits, setKidEdits] = useState({});
  const [kidSaving, setKidSaving] = useState({});
  const [kidError, setKidError] = useState({});
  const [kidSuccess, setKidSuccess] = useState({});

  // Billing state
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Delete state
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadKids = useCallback(async () => {
    try {
      const data = await apiFetch('/api/parent/kids');
      setKids(data.kids || []);
    } catch (err) { console.error(err); }
    finally { setKidsLoading(false); }
  }, [apiFetch]);

  const loadBilling = useCallback(async () => {
    try {
      const data = await apiFetch('/api/parent/billing');
      setBilling(data);
    } catch (err) { console.error(err); }
    finally { setBillingLoading(false); }
  }, [apiFetch]);

  useEffect(() => { loadKids(); loadBilling(); }, [loadKids, loadBilling]);

  // Profile handlers
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError(''); setProfileSuccess(''); setProfileSaving(true);
    try {
      await apiFetch('/api/parent/account', {
        method: 'PUT',
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email }),
      });
      setProfileSuccess('Profile updated.');
    } catch (err) { setProfileError(err.message); }
    finally { setProfileSaving(false); }
  };

  // Kid name handlers
  const handleKidEdit = (kidId, field, value) => {
    setKidEdits(prev => ({
      ...prev,
      [kidId]: { ...prev[kidId], [field]: value }
    }));
  };

  const handleSaveKidName = async (kid) => {
    const edits = kidEdits[kid.id];
    if (!edits) return;
    setKidError(prev => ({ ...prev, [kid.id]: '' }));
    setKidSuccess(prev => ({ ...prev, [kid.id]: '' }));
    setKidSaving(prev => ({ ...prev, [kid.id]: true }));
    try {
      await apiFetch(`/api/parent/kids/${kid.id}/name`, {
        method: 'PUT',
        body: JSON.stringify({
          first_name: edits.first_name ?? kid.first_name,
          last_name: edits.last_name ?? kid.last_name,
        }),
      });
      setKidSuccess(prev => ({ ...prev, [kid.id]: 'Saved.' }));
      loadKids();
    } catch (err) { setKidError(prev => ({ ...prev, [kid.id]: err.message })); }
    finally { setKidSaving(prev => ({ ...prev, [kid.id]: false })); }
  };

  // Payment portal
  const handlePortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const data = await apiFetch('/api/parent/billing/portal', { method: 'POST' });
      window.location.href = data.portal_url;
    } catch (err) { console.error(err); setPortalError(err.message || 'Failed to open payment portal. Please try again.'); }
    finally { setPortalLoading(false); }
  };

  // Password handlers
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError(''); setPasswordSuccess('');
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await apiFetch('/api/parent/password', {
        method: 'PUT',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      setPasswordSuccess('Password changed successfully.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) { setPasswordError(err.message); }
    finally { setPasswordSaving(false); }
  };

  // Delete handlers
  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (!deletePassword) {
      setDeleteError('Enter your password to confirm.');
      return;
    }
    setDeleteLoading(true);
    try {
      await apiFetch('/api/parent/account', {
        method: 'DELETE',
        body: JSON.stringify({ password: deletePassword }),
      });
      logout();
      navigate('/parent-login');
    } catch (err) { setDeleteError(err.message); }
    finally { setDeleteLoading(false); }
  };

  // Self-manage toggle
  const handleToggleSelfManage = async (kid) => {
    try {
      await apiFetch(`/api/parent/kids/${kid.id}/self-manage`, {
        method: 'PUT',
        body: JSON.stringify({ self_manage: !kid.self_manage }),
      });
      loadKids();
    } catch (err) {
      console.error('Toggle self-manage error:', err);
    }
  };

  const planLabel = billing?.plan_type === 'family' ? 'Family' : 'Individual';

  return (
    <div className="parent-dashboard">
      <header className="header">
        <div className="header-left">
          <img src="/dailyreps3.png" alt="Train Daily" className="header-logo" />
          <div className="header-title">Manage Account</div>
        </div>
        <button className="header-logout" onClick={() => navigate('/parent')}>Back</button>
      </header>

      <div style={{ padding: '16px 16px 80px', maxWidth: 600, margin: '0 auto' }}>

        {/* Section 1: Your Info */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Your Info</h3>
          <form onSubmit={handleSaveProfile}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>First Name</label>
              <input
                type="text"
                className="input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Last Name</label>
              <input
                type="text"
                className="input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Email</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
              />
            </div>
            {profileError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{profileError}</div>}
            {profileSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{profileSuccess}</div>}
            <button type="submit" className="btn btn-orange" disabled={profileSaving} style={{ width: '60%' }}>
              {profileSaving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>

        {/* Section 2: Your Players */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Your Players</h3>
          {kidsLoading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading...</div>
          ) : kids.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No players yet.</div>
          ) : kids.map(kid => (
            <div key={kid.id} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--card-border)' }}>
              {kid.team_id ? (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <input
                      type="text"
                      className="input"
                      placeholder="First name"
                      value={kidEdits[kid.id]?.first_name ?? kid.first_name ?? ''}
                      onChange={(e) => handleKidEdit(kid.id, 'first_name', e.target.value)}
                      style={{ flex: 1, padding: '10px 12px' }}
                    />
                    <input
                      type="text"
                      className="input"
                      placeholder="Last name"
                      value={kidEdits[kid.id]?.last_name ?? kid.last_name ?? ''}
                      onChange={(e) => handleKidEdit(kid.id, 'last_name', e.target.value)}
                      style={{ flex: 1, padding: '10px 12px' }}
                    />
                  </div>
                  {kid.team_name && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                      Team: {kid.team_name}
                    </div>
                  )}
                  {kidEdits[kid.id] && (
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => handleSaveKidName(kid)}
                      disabled={kidSaving[kid.id]}
                      style={{ marginTop: 4 }}
                    >
                      {kidSaving[kid.id] ? 'Saving...' : 'Save Name'}
                    </button>
                  )}
                  {kidError[kid.id] && <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 4 }}>{kidError[kid.id]}</div>}
                  {kidSuccess[kid.id] && <div style={{ color: 'var(--success)', fontSize: '0.8rem', marginTop: 4 }}>{kidSuccess[kid.id]}</div>}
                </>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{kid.generated_username}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Individual player (username cannot be changed)</div>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!kid.self_manage}
                  onChange={() => handleToggleSelfManage(kid)}
                  style={{ accentColor: 'var(--orange)' }}
                />
                Allow Player to Manage Their Own Account
              </label>
            </div>
          ))}
        </div>

        {/* Section 3: Account Level */}
        {!billingLoading && billing?.subscription && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Account Level</h3>
            <div style={{ fontSize: '0.9rem', marginBottom: 4 }}>
              <strong>{planLabel} Plan</strong>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {billing.plan_type === 'individual'
                ? `${billing.kids_count} player${billing.kids_count !== 1 ? 's' : ''} \u2022 billed per player`
                : `Up to ${billing.max_kids} players`}
            </div>
          </div>
        )}

        {/* Section 4: Payment Methods */}
        {!billingLoading && billing?.subscription && (
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 8 }}>Payment Methods</h3>
            {billing.subscription.card_last4 && (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 10 }}>
                {billing.subscription.card_brand && billing.subscription.card_brand.charAt(0).toUpperCase() + billing.subscription.card_brand.slice(1)} ending in {billing.subscription.card_last4}
              </div>
            )}
            <button
              className="btn btn-sm btn-outline"
              onClick={handlePortal}
              disabled={portalLoading}
            >
              {portalLoading ? 'Loading...' : 'Manage Payment Methods'}
            </button>
            {portalError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 8 }}>{portalError}</div>}
          </div>
        )}

        {/* Section 5: Change Password */}
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Change Password</h3>
          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Current Password</label>
              <input
                type="password"
                className="input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>New Password</label>
              <input
                type="password"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
                minLength={8}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Confirm New Password</label>
              <input
                type="password"
                className="input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{ padding: '10px 12px' }}
                required
              />
            </div>
            {passwordError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{passwordError}</div>}
            {passwordSuccess && <div style={{ color: 'var(--success)', fontSize: '0.85rem', marginBottom: 8 }}>{passwordSuccess}</div>}
            <button type="submit" className="btn btn-orange" disabled={passwordSaving} style={{ width: '60%' }}>
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Section 6: Delete Account */}
        <div className="card" style={{ marginBottom: 16, borderColor: 'var(--danger, #e74c3c)' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: 8, color: 'var(--danger, #e74c3c)' }}>Delete Account</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>
            This will permanently delete your account, all player data, and cancel any active subscriptions. This cannot be undone.
          </p>
          {!deleteConfirm ? (
            <button
              className="btn btn-sm"
              style={{ background: 'var(--danger, #e74c3c)', color: '#fff' }}
              onClick={() => setDeleteConfirm(true)}
            >
              Delete My Account
            </button>
          ) : (
            <div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>Enter your password to confirm</label>
                <input
                  type="password"
                  className="input"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  style={{ padding: '10px 12px' }}
                  placeholder="Your password"
                />
              </div>
              {deleteError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 8 }}>{deleteError}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-sm"
                  style={{ background: 'var(--danger, #e74c3c)', color: '#fff' }}
                  onClick={handleDeleteAccount}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => { setDeleteConfirm(false); setDeletePassword(''); setDeleteError(''); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
